// Developers → Gateway API: collecting via payment_mode "p2g" on the existing
// Orders API, and sending payouts. Customers only ever see SuperCash pages.
import { useState } from "react";
import { Card, Tabs, copyText } from "../../../components/sc/ui";

const BASE = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/v1`;

const SECTIONS = [
  {
    key: "p2g",
    label: "Collect (P2G)",
    method: "POST", path: "/orders",
    desc: "Already integrated with SuperCash? Change only payment_mode to \"p2g\" (pay-to-gateway). Everything else is identical to p2m/p2p: same request, same checkout_url on SuperCash's own pay page (Show QR + “Pay via UPI App”, status checked automatically, then Payment Success! / Payment Failed!), same GET /orders status, same payment.success / order.paid webhooks and signed success/failure redirects. Difference: the payment is confirmed automatically by the bank network (no phone / notification capture needed) and the full amount is credited to your SuperCash balance, which you can pay out (Payouts). Amount is whole rupees; customer.name and customer.phone are required. Optional: customer.mobile — the only number sent to the bank network (customer.phone never is) — and customer.email; name, mobile and email are passed to the bank network for its fraud/KYC checks. If SuperCash has turned on Auto Pay routing for your account, orders at or above your Auto Pay amount are processed as p2g automatically and smaller ones in the mode you sent — the response's payment_mode is the mode used (requested_payment_mode is what you sent).",
    req: `curl -X POST ${BASE}/orders \\
  -H "Authorization: Bearer sk_live_…" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 499,
    "payment_mode": "p2g",
    "receipt": "ORD-10293",
    "customer": { "name": "Asha", "phone": "+919999900000" },
    "success_url": "https://shop.example.com/pay/success",
    "failure_url": "https://shop.example.com/pay/failed",
    "notes": { "cart": "c_77" }
  }'`,
    res: `201 Created
{
  "id": "ord_a1B2c3",
  "entity": "order",
  "amount": 499,
  "currency": "INR",
  "status": "created",
  "payment_mode": "p2g",
  "checkout_url": "https://supercash.one/pay_id/a1B2c3",
  "receipt": "ORD-10293",
  …
}

→ redirect the customer to checkout_url (SuperCash page).
→ GET /orders/ord_a1B2c3 → "status": "paid" once confirmed.`,
  },
  {
    key: "p2g-events",
    label: "P2G events",
    method: "POST", path: "→ your webhook URL",
    desc: "P2G uses the webhooks you already handle (Callbacks page → Webhook endpoint, toggles respected). payment.success carries payment_mode \"p2g\", confirmation_source \"gateway\" and rrn = the bank UTR. A failed or abandoned P2G attempt sends payment.failed / payment.expired. The customer's browser is redirected to success_url / failure_url with the same signed query parameters as today.",
    req: `X-SuperCash-Event: payment.success
X-SuperCash-Signature: 5f2a…e91c

{"event":"payment.success","data":{
  "intent_id":"6740…","amount":499.0,"amount_paisa":49900,"currency":"INR",
  "payer_vpa":"9876543210@ybl","rrn":"298742840233",
  "customer_name":"Asha","customer_phone":"+919999900000",
  "short_code":"a1B2c3","payment_mode":"p2g",
  "confirmation_source":"gateway","status":"success"},"ts":"…"}`,
    res: `// Python / Flask — same verification as every SuperCash webhook
sig = hmac.new(SECRET.encode(), request.get_data(), hashlib.sha256).hexdigest()
if not hmac.compare_digest(sig, request.headers["X-SuperCash-Signature"]):
    abort(401)
# idempotent: the same event can arrive more than once
return "", 200`,
  },
  {
    key: "payout",
    label: "Create payout",
    method: "POST", path: "/payouts",
    desc: "Send money to a bank account from your SuperCash balance (P2G collections credit it). Amount is rupees (up to 2 decimals). The amount is reserved immediately; a failed payout is refunded automatically. order_id is your idempotency key (409 if reused) — on a network error, retry with the SAME order_id. 402 = insufficient balance.",
    req: `curl -X POST ${BASE}/payouts \\
  -H "Authorization: Bearer sk_live_…" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 1500.00,
    "order_id": "WD-7781",
    "beneficiary": {
      "name": "John Davis",
      "account_number": "501008736347",
      "ifsc": "HDFC0001234",
      "bank_name": "HDFC"
    },
    "customer": { "id": "user_42", "phone": "+919999900000" },
    "callback_url": "https://shop.example.com/supercash/payouts"
  }'`,
    res: `201 Created
{
  "id": "6740c3aa…",
  "order_id": "WD-7781",
  "amount": 1500.0,
  "fee": 0,
  "total_debit": 1500.0,
  "currency": "INR",
  "status": "pending",
  "beneficiary": { "name": "John Davis", "account_number": "501008736347",
                   "account_last4": "6347", "ifsc": "HDFC0001234", "bank_name": "HDFC" },
  "utr": null, "failure_reason": null, "refunded": false
}`,
  },
  {
    key: "payout-get",
    label: "Payout status & balance",
    method: "GET", path: "/payouts/{id or order_id}  ·  /payouts  ·  /balance",
    desc: "Statuses: pending → success | failed. failed payouts are refunded to your balance (failure_reason: insufficient_funds | invalid_bank_details | other). review = the bank network didn't answer when we sent it; the money stays reserved and SuperCash resolves it — don't resend. List: GET /payouts?page=1&page_size=50&status=success&date_from=2026-11-01&date_to=2026-11-30.",
    req: `curl ${BASE}/payouts/WD-7781 -H "Authorization: Bearer sk_live_…"
curl ${BASE}/balance         -H "Authorization: Bearer sk_live_…"`,
    res: `{ "id": "6740c3aa…", "order_id": "WD-7781", "status": "success",
  "utr": "HDFCN52026110312345", "amount": 1500.0, "fee": 0, … }

{ "available": 48230.55, "currency": "INR" }`,
  },
  {
    key: "payout-events",
    label: "Payout events",
    method: "POST", path: "→ payout webhook URL",
    desc: "When a payout finishes we POST payout.success or payout.failed (the same object as GET /payouts/{id}, wrapped as {event, data, ts}) to: the payout's callback_url, else the Payout webhook URL on the Callbacks page, else your main webhook URL. Signed with your normal webhook secret (X-SuperCash-Signature). Answer 2xx; otherwise we retry after 1 min, 5 min, 30 min, 2 h and 6 h.",
    req: `POST /supercash/payouts
X-SuperCash-Event: payout.success
X-SuperCash-Delivery: 6740c4…
X-SuperCash-Signature: 9ab1…

{"event":"payout.success","data":{"id":"6740c3aa…","order_id":"WD-7781",
 "status":"success","amount":1500.0,"fee":0,"utr":"HDFCN52026110312345",…},
 "ts":"2026-11-03T10:23:40Z"}`,
    res: `// Node.js / Express
const sig = crypto.createHmac("sha256", SECRET)
  .update(req.rawBody).digest("hex");
if (!crypto.timingSafeEqual(Buffer.from(sig),
    Buffer.from(req.get("X-SuperCash-Signature")))) {
  return res.status(401).end();
}
res.sendStatus(200);`,
  },
  {
    key: "errors",
    label: "Errors",
    method: "", path: "",
    desc: "Payout API errors are JSON {\"error\": {\"code\", \"message\"}}. Orders keep their existing error format ({\"detail\"}). Use sk_test_ keys while the gateway is in test mode and sk_live_ keys once live.",
    req: `Orders (p2g)
400 p2g not enabled for this account / amount limits
503 gateway not configured / payins paused

Payouts
400 invalid-amount | invalid-order-id | invalid-ifsc | invalid-account | too-small-payout | too-large-payout
401 Invalid API key
402 insufficient-balance
403 payout-not-enabled | wrong-key-mode
404 not-found
409 duplicate-order        (order_id already used)
502 network-error          (retry with the SAME order_id)
503 gateway-not-configured | payout-paused`,
    res: `{
  "error": {
    "code": "insufficient-balance",
    "message": "Insufficient balance: this payout needs ₹1,500.00"
  }
}`,
  },
];

export default function GatewayApiDocs() {
  const [tab, setTab] = useState("p2g");
  const s = SECTIONS.find((x) => x.key === tab);
  return (
    <Card title="Gateway API — P2G collections & payouts" sub={<>Base URL <span className="ui-mono">{BASE}</span> · authenticate with a secret key: <span className="ui-mono">Authorization: Bearer sk_…</span></>} data-testid="gateway-api-docs">
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Tabs value={tab} onChange={setTab} testIdPrefix="gwdocs" tabs={SECTIONS.map(({ key, label }) => ({ key, label }))} />
        {s.method && (
          <div className="ui-row">
            <span className="ui-badge" style={{ color: s.method === "GET" ? "#0f7a45" : "#4931E8" }}>{s.method}</span>
            <span className="ui-mono" style={{ fontSize: 13, fontWeight: 700 }}>{s.path}</span>
          </div>
        )}
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55 }} className="ui-muted">{s.desc}</p>
        <div className="ui-grid ui-grid-2" style={{ alignItems: "start" }}>
          <div>
            <div className="ui-between" style={{ marginBottom: 6 }}>
              <span className="ui-eyebrow">{s.key.endsWith("events") ? "What we send" : s.key === "errors" ? "Codes" : "Request"}</span>
              <button className="ui-link" style={{ fontSize: 12 }} onClick={() => copyText(s.req)}>copy</button>
            </div>
            <pre className="ui-json" style={{ maxHeight: 440 }}>{s.req}</pre>
          </div>
          <div>
            <div className="ui-between" style={{ marginBottom: 6 }}>
              <span className="ui-eyebrow">{s.key.endsWith("events") ? "Verify it" : s.key === "errors" ? "Shape" : "Response"}</span>
              <button className="ui-link" style={{ fontSize: 12 }} onClick={() => copyText(s.res)}>copy</button>
            </div>
            <pre className="ui-json" style={{ maxHeight: 440 }}>{s.res}</pre>
          </div>
        </div>
      </div>
    </Card>
  );
}
